// PURE business logic — no framework imports.
//
// The single home for "is this hostname an internal NAME", and for the
// normalisation every host check must do before it looks at anything.
//
// Same reason the range table next door lives here (`private-ip.ts`): the
// API-boundary schemas in `packages/types` apply the guard and cannot import
// backend code, and the backend's delivery-time guard applies the same one. Two
// hand-maintained copies is how they drift, and these two HAD drifted — see
// below.
//
// ## What was wrong, and why the normalisation is the load-bearing half
//
// `safeExternalUrlSchema` compared `hostname.toLowerCase()` against an exact set
// of two names. `isSafeWebhookUrl` lower-cased, stripped a trailing dot, and
// tested four suffix rules. So the same URL was refused by one guard and
// accepted by the other:
//
//   https://localhost./       accepted by the schema, refused by the webhook guard
//   https://foo.internal/     accepted by the schema, refused by the webhook guard
//
// A trailing dot is a legal absolute-FQDN spelling and resolves identically, so
// `localhost.` reaches the same loopback `localhost` does. The schema's stored
// URL is later fetched server-side with the organization's DECRYPTED API key
// attached, which makes the gap a server-side request forgery primitive with
// credential exposure rather than a validation nicety.
//
// 🚨 THE NAME LOOKUP IS THE LIVE HALF. Measured against 24 host spellings: 6 were
// accepted that must be refused, and every one of them was a NAME — three from
// the missing trailing-dot strip, three from testing exact names instead of
// suffixes.
//
// ⚠️ An IP literal with a trailing dot is NOT among them, and the ticket predicted
// it would be. The WHATWG URL parser canonicalises IP literals before any guard
// sees them — it drops the dot from `169.254.169.254.` and rewrites `2130706433`,
// `0x7f000001`, `0177.0.0.1` and `127.1` all to dotted-quad — while keeping the
// dot on a NAME. That asymmetry is the whole reason this is a name-shaped bug.
// Normalising before the IP parse is still correct, and it is DEFENSIVE rather
// than load-bearing; the schema says so where it does it.

/**
 * Lower-case a hostname and strip EVERY trailing dot.
 *
 * All of them, not one. `new URL()` preserves `localhost..` verbatim, and while
 * a label-less name does not resolve, a guard that is strict costs nothing and a
 * guard that is nearly strict is an argument waiting to be had. The webhook
 * guard stripped exactly one before this module existed.
 *
 * Brackets are left alone: an IPv6 literal keeps them in `URL.hostname`, and the
 * callers strip those themselves before handing the address to a range check.
 */
export function normalizeHostname(hostname: string): string {
  return hostname.toLowerCase().replace(/\.+$/, "");
}

/**
 * Names that are never a legitimate external endpoint, independent of any IP
 * range.
 *
 * `metadata.google.internal` is the GCP metadata server and resolves to
 * 169.254.169.254; blocking the NAME as well as the address means a DNS answer
 * is not the only thing standing in the way. It is also matched by the
 * `.internal` suffix below — kept explicit because the reason to block it is its
 * own, and a future narrowing of the suffix list must not silently unblock it.
 */
const BLOCKED_HOSTNAMES = new Set(["localhost", "metadata.google.internal"]);

/**
 * Suffixes reserved for names that never leave the local network.
 *
 * `.local` is mDNS (RFC 6762), `.localhost` is the loopback TLD (RFC 6761), and
 * `.internal` is the convention cloud providers use for private zones — GCP's
 * metadata server among them.
 */
const BLOCKED_SUFFIXES = [".localhost", ".local", ".internal"] as const;

/**
 * Whether a hostname names an internal endpoint, by NAME rather than by address.
 *
 * 🚨 TAKES AN ALREADY-NORMALISED HOST, and does NOT normalise again. Every caller
 * needs the normalised value anyway — for the IPv6 bracket strip and the IPv4
 * parse that follow — so normalising here too would do it twice on every path.
 *
 * That is not merely redundant, IT HIDES BUGS. Measured: with a defect that
 * stripped ONE trailing dot instead of all of them, `localhost..` still came out
 * blocked, because two applications stripped two dots. The mutation that should
 * have exposed the defect passed all 112 conformance cases. One normalisation per
 * path is what keeps the guard's behaviour equal to the guard's code.
 *
 * Says nothing about IP literals — the caller pairs this with the range table in
 * `private-ip.ts`.
 */
export function isInternalHostname(normalizedHostname: string): boolean {
  if (BLOCKED_HOSTNAMES.has(normalizedHostname)) return true;
  return BLOCKED_SUFFIXES.some((suffix) => normalizedHostname.endsWith(suffix));
}
